import { state, subscribe } from '../../state/store.js';
import { formatPrice, escapeHtml } from '../../lib/format.js';
import { MINIMUM_ORDER_VALUE } from '../../config/settings.js';
import { isUnavailable, exceedsStock } from './cart.js';

/**
 * Only lines the customer can actually buy count towards the totals. A
 * sold-out line stays visible (marked, with a remove action) but must not be
 * charged for — otherwise the displayed total would not match the order the
 * server is willing to create.
 */
function payableLines() {
  return (state.cart.lines || []).filter((l) => !isUnavailable(l));
}

function computeSubtotal() {
  return payableLines().reduce((a, it) => a + it.price * it.qty, 0);
}

/**
 * Discount is sourced from Shopify when we have a live cart (state.cart.discount
 * comes from cartDiscountCodesUpdate), and falls back to the local promo math
 * when running on fallback data with no Shopify connection.
 */
function computeDiscount(subtotal) {
  // Server-computed discount (Shopify) — preferred. But `cart.discount` is a
  // WHOLE-cart figure, including any share apportioned to lines we've excluded
  // as unbuyable. Subtracting it from a payable-only subtotal would understate
  // the total (and could clamp it to 0), then make it jump UP once the customer
  // removes the sold-out item. Re-derive from the payable lines when any line
  // is excluded; the per-line costs come from shapeCart.
  const lines = state.cart.lines || [];
  if (lines.some(isUnavailable)) {
    const perLine = payableLines().reduce(
      (a, l) => a + (Number(l.lineSubtotal || 0) - Number(l.lineTotal || 0)),
      0,
    );
    if (perLine > 0) return +perLine.toFixed(2);
    // No per-line cost data (dev fallback) — better to show no discount than
    // one computed against items that aren't being charged for.
    return 0;
  }
  if (typeof state.cart.discount === 'number' && state.cart.discount > 0) {
    return state.cart.discount;
  }
  // Local fallback for offline/dev: state.promo is set by promo.js.
  if (!state.promo) return 0;
  if (state.promo.type === 'percent') return subtotal * (state.promo.value / 100);
  if (state.promo.type === 'fixed')   return Math.min(subtotal, state.promo.value);
  return 0;
}

function render() {
  const subtotal = computeSubtotal();
  const discount = computeDiscount(subtotal);
  const totalQty = payableLines().reduce((a, i) => a + i.qty, 0);

  const titleEl = document.getElementById('totals-title');
  if (titleEl) titleEl.textContent = `Деталі замовлення (${totalQty} шт.)`;

  const li = document.getElementById('lineitems');
  if (li) {
    li.innerHTML = payableLines().map((it) => `
      <div class="total-row lineitem">
        <span class="lbl">${it.qty} × ${escapeHtml(it.name)}</span>
        <span class="val">${formatPrice(it.price * it.qty)}</span>
      </div>
    `).join('');
  }

  const sub = document.getElementById('subtotal');
  if (sub) sub.textContent = formatPrice(subtotal);

  const dRow = document.getElementById('discount-row');
  if (dRow) {
    if (discount > 0) {
      dRow.style.display = '';
      const v = document.getElementById('discount-val');
      if (v) v.textContent = '− ' + formatPrice(discount);
    } else {
      dRow.style.display = 'none';
    }
  }

  // Shipping placeholder until carrier API returns a real rate.
  const ship = document.getElementById('shipping-val');
  if (ship) ship.textContent = 'За тарифами перевізника';

  const grandTotal = Math.max(0, subtotal - discount);
  const grandEl = document.getElementById('grand-total');
  if (grandEl) grandEl.textContent = formatPrice(grandTotal);

  // Minimum-order gate: show a notice + disable the pay button when the
  // grand total is below the configured floor. Once the customer crosses
  // the threshold both go back to normal.
  const notice = document.getElementById('min-order-notice');
  const payBtn = document.getElementById('pay-btn');
  const belowMinimum = MINIMUM_ORDER_VALUE > 0
    && grandTotal > 0  // empty cart handled separately, no need to nag here
    && grandTotal < MINIMUM_ORDER_VALUE;
  // Checkout is refused server-side while the cart holds a line that can't be
  // bought (routes/order.js findUnsellableLines). Reflect that in the button
  // up front instead of letting the customer discover it on submit — the
  // cart section renders the banner explaining what to fix.
  const hasBlockingLine = (state.cart.lines || []).some((l) => isUnavailable(l) || exceedsStock(l));
  // If the cart never loaded we do not know what the customer is buying —
  // POST /api/order would 400 on the missing cartId anyway, so refuse up front.
  const cartUnknown = !!state.ui?.cartError;
  if (notice) {
    if (belowMinimum) {
      const shortBy = MINIMUM_ORDER_VALUE - grandTotal;
      notice.innerHTML = `Мінімальна сума замовлення — <strong>${formatPrice(MINIMUM_ORDER_VALUE)}</strong>. Додайте ще <strong>${formatPrice(shortBy)}</strong> до кошика.`;
      notice.style.display = '';
    } else {
      notice.style.display = 'none';
    }
  }
  if (payBtn) {
    payBtn.disabled = belowMinimum || hasBlockingLine || cartUnknown || !!state.ui?.submitting;
    payBtn.classList.toggle('is-disabled', belowMinimum || hasBlockingLine || cartUnknown);
  }
}

export function initTotals() {
  render();
  subscribe(['cart', 'promo', 'delivery', 'ui'], render);
}
